import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreatePortalOrderBody, SetPortalQuoteStatusBody } from '@malash-office/types';
import { PORTAL_KEY } from './portalKeys';
import {
  createPortalCategory,
  createPortalOrder,
  createPortalTag,
  fetchPortalCategories,
  fetchPortalFiles,
  fetchPortalMe,
  fetchPortalMessages,
  fetchPortalOrder,
  fetchPortalOrders,
  fetchPortalOwners,
  fetchPortalProducts,
  fetchPortalProjectTypes,
  fetchPortalTags,
  markPortalThreadRead,
  sendPortalMessage,
  setPortalQuoteStatus,
  uploadPortalFile
} from './portal.api';
import type { PortalOrderListParams } from './portal.types';

/** The portal's hooks. Its own cache prefix — see `portalKeys.ts`. */
export { PORTAL_KEY } from './portalKeys';

/**
 * Who the portal thinks you are.
 *
 * Long-lived: the answer changes only when somebody at our end links or unlinks a contact, and the
 * shell prints the organisation's name on every screen.
 */
export const usePortalMe = () =>
  useQuery({ queryKey: [PORTAL_KEY, 'me'], queryFn: fetchPortalMe, staleTime: 10 * 60 * 1000 });

export const usePortalOrders = (params: PortalOrderListParams) =>
  useQuery({ queryKey: [PORTAL_KEY, 'orders', params], queryFn: () => fetchPortalOrders(params) });

export const usePortalOrder = (id: number | null) =>
  useQuery({
    queryKey: [PORTAL_KEY, 'order', id],
    queryFn: () => fetchPortalOrder(id as number),
    enabled: !!id
  });

/** Sets a quote's status from the portal; the answer is the order itself, written straight to the cache. */
export const useSetPortalQuoteStatus = (orderId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ quoteId, body }: { quoteId: number; body: SetPortalQuoteStatusBody }) =>
      setPortalQuoteStatus(quoteId, body),
    onSuccess: order => {
      queryClient.setQueryData([PORTAL_KEY, 'order', orderId], order);
      // The thread got a line about it, and the table's row may have moved.
      queryClient.invalidateQueries({ queryKey: [PORTAL_KEY, 'messages', orderId] });
      queryClient.invalidateQueries({ queryKey: [PORTAL_KEY, 'orders'] });
    }
  });
};

export const usePortalMessages = (id: number | null) =>
  useQuery({
    queryKey: [PORTAL_KEY, 'messages', id],
    queryFn: () => fetchPortalMessages(id as number),
    enabled: !!id
  });

export const usePortalFiles = (id: number | null) =>
  useQuery({
    queryKey: [PORTAL_KEY, 'files', id],
    queryFn: () => fetchPortalFiles(id as number),
    enabled: !!id
  });

/** The two pickers on the order form. Rarely change, so they are cached for the session's length. */
export const usePortalProjectTypes = () =>
  useQuery({
    queryKey: [PORTAL_KEY, 'project-types'],
    queryFn: fetchPortalProjectTypes,
    staleTime: 5 * 60 * 1000
  });

export const usePortalCategories = () =>
  useQuery({
    queryKey: [PORTAL_KEY, 'categories'],
    queryFn: fetchPortalCategories,
    staleTime: 5 * 60 * 1000
  });

export const usePortalTags = () =>
  useQuery({ queryKey: [PORTAL_KEY, 'tags'], queryFn: fetchPortalTags, staleTime: 5 * 60 * 1000 });

export const useCreatePortalTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createPortalTag(name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PORTAL_KEY, 'tags'] })
  });
};

export const usePortalOwners = () =>
  useQuery({ queryKey: [PORTAL_KEY, 'owners'], queryFn: fetchPortalOwners, staleTime: 5 * 60 * 1000 });

/** Fetched only while the chosen type asks for an earlier product — see the order form. */
export const usePortalProducts = (enabled: boolean) =>
  useQuery({ queryKey: [PORTAL_KEY, 'products'], queryFn: fetchPortalProducts, enabled, staleTime: 60 * 1000 });

export const useCreatePortalOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePortalOrderBody) => createPortalOrder(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PORTAL_KEY] })
  });
};

export const useCreatePortalCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createPortalCategory(name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PORTAL_KEY, 'categories'] })
  });
};

/**
 * A message the customer sends.
 *
 * Invalidates the order as well as the thread: `lastActivityAt` is what the tracking table's last
 * column prints, and a message they just sent moving that row is the feedback that it arrived.
 */
export const useSendPortalMessage = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => sendPortalMessage(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PORTAL_KEY, 'messages', id] });
      queryClient.invalidateQueries({ queryKey: [PORTAL_KEY, 'orders'] });
    }
  });
};

export const useMarkPortalThreadRead = () =>
  useMutation({ mutationFn: (id: number) => markPortalThreadRead(id) });

export const useUploadPortalFile = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, name }: { file: File; name: string }) => uploadPortalFile(id, file, name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PORTAL_KEY, 'files', id] })
  });
};
